import { VotingMachineProposalState } from '@aave/contract-helpers';
import { normalizeBN } from '@aave/math-utils';
import {
  ProposalDetail,
  ProposalVote,
  SimplifiedProposal,
} from 'src/services/GovernanceCacheService';
import { governanceV3Config } from 'src/ui-config/governanceConfig';

import { ProposalBadgeState } from './StateBadge';
import {
  ProposalDetailDisplay,
  ProposalListItem,
  ProposalVoteDisplayInfo,
  VoteDisplay,
  VoteProposalData,
} from './types';

// ============================================
// Badge -> cache state mapping (the inverse, for the server-side state filter)
// ============================================

export function badgeToCacheState(badge: ProposalBadgeState): string {
  switch (badge) {
    case ProposalBadgeState.Created:
      return 'created';
    case ProposalBadgeState.OpenForVoting:
      return 'active';
    case ProposalBadgeState.Passed:
      return 'queued';
    case ProposalBadgeState.Executed:
      return 'executed';
    case ProposalBadgeState.Failed:
      return 'failed';
    case ProposalBadgeState.Cancelled:
      return 'cancelled';
    case ProposalBadgeState.Expired:
      return 'expired';
    case ProposalBadgeState.PartiallyExecuted:
      return 'partially_executed';
  }
}

// ============================================
// Cache state -> badge mapping (replaces mapStateToBadge duplicated in 3 cache files)
// ============================================

export function cacheStateToBadge(state: string): ProposalBadgeState {
  switch (state) {
    case 'created':
      return ProposalBadgeState.Created;
    case 'active':
      return ProposalBadgeState.OpenForVoting;
    case 'queued':
      return ProposalBadgeState.Passed;
    case 'executed':
      return ProposalBadgeState.Executed;
    case 'failed':
      return ProposalBadgeState.Failed;
    case 'cancelled':
      return ProposalBadgeState.Cancelled;
    case 'expired':
      return ProposalBadgeState.Expired;
    case 'partially_executed':
      return ProposalBadgeState.PartiallyExecuted;
    default:
      return ProposalBadgeState.Created;
  }
}

// ============================================
// Vote calculation helpers
// ============================================

/** Convert 18-decimal vote count string to human-readable number */
function formatVotes(votes: string): number {
  return normalizeBN(votes || '0', 18).toNumber();
}

/**
 * Calculate unified vote display info from raw cache vote strings.
 * Returns percentages in 0-1 range (matching VoteBar expectations).
 */
export function calculateCacheVoteDisplayInfo(
  votesFor: string,
  votesAgainst: string,
  quorum: string | null,
  requiredDifferential: string | null
): ProposalVoteDisplayInfo {
  const forVotes = formatVotes(votesFor);
  const againstVotes = formatVotes(votesAgainst);
  const total = forVotes + againstVotes;

  // Quorum is stored in whole token units (e.g., 320000 = 320K tokens)
  const quorumValue = quorum ? parseFloat(quorum) : 0;
  const differentialValue = requiredDifferential ? parseFloat(requiredDifferential) : 0;
  const currentDifferential = forVotes - againstVotes;

  return {
    forVotes,
    againstVotes,
    // 0-1 range for VoteBar
    forPercent: total > 0 ? forVotes / total : 0,
    againstPercent: total > 0 ? againstVotes / total : 0,
    quorum: quorumValue,
    quorumReached: forVotes >= quorumValue,
    currentDifferential,
    requiredDifferential: differentialValue,
    differentialReached: currentDifferential >= differentialValue,
  };
}

// ============================================
// VoteProposalData builders
// ============================================

/**
 * Parse hex bytes into a clean 0x-prefixed hex string.
 * Handles three formats:
 *   - rindexer FixedBytes wrapper: "FixedBytes(0xabc123..., 32)" → "0xabc123..."
 *   - PostgreSQL encode(bytea, 'hex') output: "abc123..." → "0xabc123..."
 *   - Already prefixed: "0xabc123..." → "0xabc123..."
 */
function parseFixedBytes(raw: string | null): string | null {
  if (!raw) return null;
  const match = raw.match(/FixedBytes\((0x[a-fA-F0-9]+)/);
  if (match) return match[1];
  if (/^[a-fA-F0-9]+$/.test(raw)) return `0x${raw}`;
  return raw;
}

/** Map a VotingMachine contract address to its chain ID via governance config. */
function votingMachineAddressToChainId(address: string): number | undefined {
  const lowerAddress = address.toLowerCase();
  for (const [chainId, config] of Object.entries(governanceV3Config.votingChainConfig)) {
    if (config.votingMachineAddress.toLowerCase() === lowerAddress) {
      return Number(chainId);
    }
  }
  return undefined;
}

/** Build VoteProposalData from a cache ProposalDetail. Returns undefined if voting chain can't be determined. */
export function buildVoteProposalFromCache(
  detail: ProposalDetail,
  userVote?: ProposalVote | null
): VoteProposalData | undefined {
  if (!detail.votingMachineAddress) return undefined;

  const votingMachineChainId = votingMachineAddressToChainId(detail.votingMachineAddress);
  if (votingMachineChainId === undefined) return undefined;

  const { aaveTokenAddress, aAaveTokenAddress, stkAaveTokenAddress } =
    governanceV3Config.votingAssets;

  const snapshotBlockHash = parseFixedBytes(detail.snapshotBlockHash);
  if (!snapshotBlockHash) return undefined;

  return {
    proposalId: detail.id,
    snapshotBlockHash,
    votingMachineChainId,
    votingAssets: [aaveTokenAddress, aAaveTokenAddress, stkAaveTokenAddress],
    votingState:
      detail.state === 'active'
        ? VotingMachineProposalState.Active
        : VotingMachineProposalState.Finished,
    votedInfo:
      userVote && userVote.votingPower !== '0'
        ? { support: userVote.support, votingPower: userVote.votingPower }
        : undefined,
  };
}

// ============================================
// Cache -> canonical adapters
// ============================================

export function adaptCacheProposalToListItem(p: SimplifiedProposal): ProposalListItem {
  const voteInfo = calculateCacheVoteDisplayInfo(p.votesFor, p.votesAgainst, null, null);
  return {
    id: p.id,
    title: p.title,
    shortDescription: p.shortDescription,
    author: p.author,
    badgeState: cacheStateToBadge(p.state),
    voteInfo,
  };
}

export function adaptCacheProposalToDetail(
  p: ProposalDetail,
  userVote?: ProposalVote | null
): ProposalDetailDisplay {
  const voteInfo = calculateCacheVoteDisplayInfo(
    p.votesFor,
    p.votesAgainst,
    p.quorum,
    p.requiredDifferential
  );
  return {
    id: p.id,
    title: p.title,
    shortDescription: p.shortDescription,
    description: p.description,
    author: p.author,
    discussions: p.discussions,
    ipfsHash: p.ipfsHash,
    badgeState: cacheStateToBadge(p.state),
    voteInfo,
    rawCacheDetail: p,
    voteProposalData: buildVoteProposalFromCache(p, userVote),
  };
}

// ============================================
// Vote adapters
// ============================================

/** Adapt cache vote (votingPower in wei/18 decimals) to normalized VoteDisplay */
export function adaptCacheVote(vote: ProposalVote): VoteDisplay {
  return {
    voter: vote.voter,
    support: vote.support,
    votingPower: normalizeBN(vote.votingPower, 18).toString(),
  };
}
